import { useState } from "react";
import { Phone, Mail, MapPin, Clock, MessageCircle, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { COMPANY } from "@/lib/data";
import { submitContact } from "@/lib/api";
import { Reveal, Cascade } from "@/components/Reveal";

const INFO = [
  { icon: Phone, label: "Telefon", value: COMPANY.phone, href: `tel:${COMPANY.phoneRaw}` },
  { icon: Mail, label: "E-posta", value: COMPANY.email, href: `mailto:${COMPANY.email}` },
  { icon: MapPin, label: "Adres", value: COMPANY.address, href: COMPANY.mapLink },
  { icon: Clock, label: "Çalışma Saatleri", value: COMPANY.hours },
];

export default function Contact() {
  const [form, setForm] = useState({ name: "", phone: "", email: "", message: "" });
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.email) {
      toast.error("Lütfen ad, telefon ve e-posta alanlarını doldurun.");
      return;
    }
    setLoading(true);
    try {
      await submitContact(form);
      toast.success("Mesajınız alındı! En kısa sürede size dönüş yapacağız.");
      setForm({ name: "", phone: "", email: "", message: "" });
    } catch {
      toast.error("Bir hata oluştu. Lütfen tekrar deneyin.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full rounded-xl border border-ink/10 bg-white px-4 py-3.5 text-sm text-ink outline-none transition-[border-color,box-shadow] duration-200 placeholder:text-ink/35 focus:border-orange focus:ring-4 focus:ring-orange/10";

  return (
    <section id="iletisim" className="scroll-mt-24" data-testid="contact">
      <div className="container-shell">
        <Reveal className="max-w-2xl">
          <p className="overline">İletişim</p>
          <h2 className="heading-lg mt-4">
            <Cascade text="Dijital dönüşüme {bugün} başlayın." />
          </h2>
        </Reveal>

        <div className="mt-[56px] grid grid-cols-1 gap-[40px] lg:grid-cols-2 lg:gap-16">
          {/* Form + WhatsApp */}
          <Reveal>
            <form
              onSubmit={onSubmit}
              className="rounded-3xl border border-ink/5 bg-white p-8 shadow-[0_8px_30px_rgba(32,32,32,0.06)] md:p-10"
              data-testid="contact-form"
            >
              <div className="space-y-4">
                <input
                  className={inputCls}
                  placeholder="Ad Soyad *"
                  value={form.name}
                  onChange={update("name")}
                  data-testid="contact-name"
                />
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <input
                    className={inputCls}
                    placeholder="Telefon *"
                    value={form.phone}
                    onChange={update("phone")}
                    data-testid="contact-phone"
                  />
                  <input
                    className={inputCls}
                    type="email"
                    placeholder="E-posta *"
                    value={form.email}
                    onChange={update("email")}
                    data-testid="contact-email"
                  />
                </div>
                <textarea
                  className={`${inputCls} min-h-[130px] resize-none`}
                  placeholder="Mesajınız"
                  value={form.message}
                  onChange={update("message")}
                  data-testid="contact-message"
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                className="btn-primary mt-6 w-full disabled:opacity-70"
                data-testid="contact-submit"
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                {loading ? "Gönderiliyor..." : "Mesaj Gönder"}
              </button>
            </form>

            <a
              href={`https://wa.me/${COMPANY.whatsapp}`}
              target="_blank"
              rel="noreferrer"
              className="mt-4 flex items-center justify-center gap-2 rounded-2xl bg-[#25D366] px-6 py-4 font-semibold text-white transition-transform duration-300 hover:-translate-y-0.5"
              data-testid="whatsapp-btn"
            >
              <MessageCircle className="h-5 w-5" /> WhatsApp&apos;tan Yazın
            </a>
          </Reveal>

          {/* Info + Map */}
          <Reveal delay={0.1}>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              {INFO.map((item) => {
                const Wrap = item.href ? "a" : "div";
                return (
                  <Wrap
                    key={item.label}
                    href={item.href}
                    target={item.href?.startsWith("http") ? "_blank" : undefined}
                    rel="noreferrer"
                    className="rounded-2xl border border-ink/5 bg-cloud p-6 transition-[transform,box-shadow] duration-300 hover:-translate-y-1 hover:shadow-[0_12px_30px_rgba(32,32,32,0.06)]"
                    data-testid={`contact-info-${item.label}`}
                  >
                    <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-white text-orange shadow-sm">
                      <item.icon className="h-5 w-5" strokeWidth={1.6} />
                    </span>
                    <p className="mt-4 text-xs font-bold uppercase tracking-wider text-ink/40">
                      {item.label}
                    </p>
                    <p className="mt-1 text-sm font-semibold leading-snug text-ink">
                      {item.value}
                    </p>
                  </Wrap>
                );
              })}
            </div>
            <div className="mt-4 overflow-hidden rounded-2xl border border-ink/5">
              <iframe
                title="TEKNO ŞURA konum"
                src={COMPANY.mapEmbed}
                width="100%"
                height="280"
                style={{ border: 0, filter: "grayscale(0.3) contrast(1.05)" }}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                data-testid="contact-map"
              />
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
